import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { canBrowseLibrary } from '@/lib/libraryAccess';
import { useLibraryBranding } from '@/lib/libraryBranding';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, FileText, Copy, Archive, Eye, Edit, MoreVertical, Send, UserPlus, Library } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STATUS_COLORS = { DRAFT: 'secondary', PUBLISHED: 'default', ARCHIVED: 'outline' };

export default function FormTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const brand = useLibraryBranding();

  // Assign dialog state
  const [assignDialog, setAssignDialog] = useState(null); // template object or null
  const [participants, setParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [assignForm, setAssignForm] = useState({ target_id: '', due_date: '', priority: 'NORMAL', approval_required: false });
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/forms/templates?${params}`);
      setTemplates(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Pre-load participants for assignment
  useEffect(() => {
    api.get('/participants?pageSize=100').then(({ data }) => setParticipants(data.items || [])).catch(() => {});
  }, []);

  const handleDuplicate = async (id) => {
    try { await api.post(`/forms/templates/${id}/duplicate`); toast.success('Template duplicated'); load(); } catch { toast.error('Failed to duplicate'); }
  };
  const handleArchive = async (id) => {
    try { await api.post(`/forms/templates/${id}/archive`); toast.success('Template archived'); load(); } catch { toast.error('Failed to archive'); }
  };
  const handlePublish = async (id) => {
    try { await api.post(`/forms/templates/${id}/publish`); toast.success('Template published'); load(); } catch { toast.error('Failed to publish'); }
  };

  const openAssign = (template) => {
    setAssignDialog(template);
    setAssignForm({ target_id: '', due_date: '', priority: 'NORMAL', approval_required: false });
    setParticipantSearch('');
  };

  const handleAssign = async () => {
    if (!assignForm.target_id) { toast.error('Please select a participant'); return; }
    setAssigning(true);
    try {
      await api.post('/forms/assignments', {
        template_id: assignDialog.id,
        target_type: 'PARTICIPANT',
        target_id: assignForm.target_id,
        due_date: assignForm.due_date || null,
        priority: assignForm.priority,
        approval_required: assignForm.approval_required,
      });
      toast.success('Form assigned to participant');
      setAssignDialog(null);
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.error?.message || d?.message || (typeof d === 'string' ? d : 'Failed to assign')); }
    setAssigning(false);
  };

  const isSelectable = (t) => ['PLATFORM_GLOBAL_CONFIRMED', 'GETCARE360_OWNED_CONFIRMED'].includes(t.tenant_ownership_status);

  const filteredTemplates = templates.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.form_code?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredParticipants = participants.filter(p => {
    if (!participantSearch) return true;
    const q = participantSearch.toLowerCase();
    return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.ndis_number?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="form-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Form Templates</h1>
          <p className="text-muted-foreground mt-1">{total} templates total</p>
        </div>
        <div className="flex items-center gap-2">
          {canBrowseLibrary(hasPermission) && (
            <Button variant="outline" onClick={() => navigate('/forms/library')} data-testid="import-from-library-btn">
              <Library className="w-4 h-4 mr-2" />Import from Library
            </Button>
          )}
          <Button onClick={() => navigate('/forms/templates/new')} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" />New Template
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search templates..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="template-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PUBLISHED">Published</SelectItem>
            <SelectItem value="ARCHIVED">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filteredTemplates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No templates found</p>
          <p className="text-sm mt-1">Create your first form template to get started</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filteredTemplates.map(t => (
            <Card key={t.id} className="hover:shadow-sm transition-shadow" data-testid={`template-row-${t.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => navigate(`/forms/templates/${t.id}`)}>
                  <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.form_code} · {t.module_type || 'GENERAL'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={STATUS_COLORS[t.status] || 'secondary'}>{t.status}</Badge>
                  {t.origin === 'LIBRARY_IMPORT' && <Badge variant="outline" className="text-[10px]" title={`Imported from ${brand.packLabel(t.origin_ref?.pack_version)}`} data-testid={`library-origin-badge-${t.id}`}><Library className="w-3 h-3 mr-1" />Library</Badge>}
                  {!isSelectable(t) && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300" data-testid={`ownership-review-badge-${t.id}`}>Needs ownership review</Badge>}
                  {t.status === 'PUBLISHED' && isSelectable(t) && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => openAssign(t)} data-testid={`assign-btn-${t.id}`}>
                      <UserPlus className="w-3.5 h-3.5 mr-1.5" />Assign
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`template-actions-${t.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/forms/templates/${t.id}`)}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/forms/templates/${t.id}/edit`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                      {t.status === 'PUBLISHED' && isSelectable(t) && <DropdownMenuItem onClick={() => openAssign(t)}><UserPlus className="w-4 h-4 mr-2" />Assign to Participant</DropdownMenuItem>}
                      {t.status === 'DRAFT' && <DropdownMenuItem onClick={() => handlePublish(t.id)}><Send className="w-4 h-4 mr-2" />Publish</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => handleDuplicate(t.id)}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>
                      {t.status !== 'ARCHIVED' && <DropdownMenuItem onClick={() => handleArchive(t.id)} className="text-red-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Assign to Participant Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={v => { if (!v) setAssignDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Form to Participant</DialogTitle>
            {assignDialog && <p className="text-sm text-muted-foreground mt-1">{assignDialog.name}</p>}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Participant *</Label>
              <Input placeholder="Search by name or NDIS number..." value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} className="mb-2" data-testid="participant-search-input" />
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {filteredParticipants.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No participants found</p>
                ) : filteredParticipants.map(p => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 border-b last:border-0 transition-colors ${assignForm.target_id === p.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}
                    onClick={() => setAssignForm(f => ({ ...f, target_id: p.id }))}
                    data-testid={`participant-option-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                      {(p.first_name?.[0] || '')}{(p.last_name?.[0] || '')}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{p.first_name} {p.last_name}</p>
                      <p className="text-xs text-muted-foreground">NDIS: {p.ndis_number || 'N/A'}</p>
                    </div>
                    {assignForm.target_id === p.id && <Badge variant="default" className="ml-auto text-xs">Selected</Badge>}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={assignForm.due_date} onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))} data-testid="assign-due-date" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={assignForm.priority} onValueChange={v => setAssignForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="assign-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning || !assignForm.target_id} data-testid="confirm-assign-btn">
              <UserPlus className="w-4 h-4 mr-2" />{assigning ? 'Assigning...' : 'Assign Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
